import { token } from '@atlaskit/tokens';
import ThreadPopup from '@databases/comments/ThreadPopup';
import { useCommentsVisibility } from '@databases/comments/hooks';
import {
  useCommentActionsContext,
  useCommentStateContext
} from '@databases/comments/hooks/useCommentContext';
import { useAiSummaryCellAction } from '@databases/database-ai-fields';
import { getVersionId } from '@databases/database-state';
import { EditMode, getFieldType } from '@databases/fields';
import { cellInlineEditMetric } from '@databases/frontend-metrics/metrics/cell-inline-edit';
import { useMetric } from '@databases/frontend-metrics/use-metric';
import {
  InlineEditContainer,
  InlineEditStateless
} from '@databases/inline-edit/InlineEdit';
import InlineEditWrapper from '@databases/inline-edit/InlineEditWrapper';
import { messages } from '@databases/intl/messages';
import { subscribeTo, key } from '@databases/ping-registry';
import { usePresence } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { type Adjacents } from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getService } from '@databases/service-container';
import { useSkeletonEnabled } from '@databases/skeleton';
import { DatabaseValueViewContainer } from '@databases/styles';
import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue
} from '@databases/utils/database/utils';
import {
  selectUnresolvedThreadsByValueIdOptional,
  selectUnresolvedThreadsCommentCount
} from '@databases/utils/redux/comments/selectors';
import { getValueId } from '@databases/utils/utils';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { getExperimentValue } from '@canvas/client-feature-flags';

import DatabaseValueEdit from './DatabaseValueEdit.tsx';
import DatabaseValueView from './DatabaseValueView.tsx';
import AiSummaryFieldReadView from './ai-summary/ai-summary-field-read-view.tsx';
import { AiSummaryProcessingBoundary } from './ai-summary/ai-summary-processing-boundary.tsx';

export const ValidationStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const StyledInlineEditWrapper = styled(InlineEditWrapper)<{
  isSelected?: boolean;
  isFirstInRow?: boolean;
  matchHeaderPadding?: boolean;
  selectedAdjacentCells?: Adjacents;
  hasParticipants?: boolean;
  isReadonly?: boolean;
}>`
  height: 100%;

  > ${ValidationStatusContainer},
  > ${ValidationStatusContainer}
  > ${InlineEditContainer} {
    flex-grow: 1;

    *:not(a):not(span):not(#comment-threads-popup):not(#comment-threads-popup *):not(
      #popover-edit-container-multi-select-value
    ):not([data-rich-text-edit-wrapper]) {
      // Among other things, this hides the border of input fields because we
      // want to use the border of the BorderContainer. We want to use the smart
      // card-provided border for smart cards, so we exclude <a> and <span>. In
      // NCTD-2440, we want to look into improving this styling.
      // Also excludes comments related elements.
      border-color: ${({ hasParticipants, isReadonly }) =>
        `${hasParticipants && !isReadonly ? '' : 'transparent'}`};
    }
  }

  &:hover {
    > ${ValidationStatusContainer} > ${InlineEditContainer} {
      background: ${({ isSelected, isReadonly }) =>
        isSelected || isReadonly
          ? 'initial'
          : token('elevation.surface.hovered', '#F1F2F4')};
    }
  }

  > ${DatabaseValueViewContainer},
  > div
  > ${DatabaseValueViewContainer},
  > ${ValidationStatusContainer}
  > ${InlineEditContainer}
  div
  > ${DatabaseValueViewContainer} {
    display: inline-block;
    padding: ${({ matchHeaderPadding }) =>
      matchHeaderPadding ? `10px 8px 10px ${token('space.150', '12px')}` : '10px 8px'};
    max-width: 100%;
    box-sizing: border-box;
  }

  min-height: 41px !important;

  && textarea {
    padding: ${({ matchHeaderPadding }) =>
      matchHeaderPadding ? `8px 6px 8px 10px` : '8px 6px'};
  }

  && input {
    padding: ${({ matchHeaderPadding }) =>
      matchHeaderPadding ? `8px 6px 8px 10px` : undefined};
  }
`;

/**
 * This is a clone of StyledInlineEditWrapper, however it is not dependent on
 * child components. This is so the database skeleton loader can load early
 * on without other components that depend on state.
 */
// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const StyledInlineEditWrapperWithoutComponents = styled.div`
  height: 100%;
  min-height: 41px !important;
  padding: ${token('space.150', '8px')} 0 0 ${token('space.100', '8px')};

  && textarea {
    padding: ${token('space.100', '8px')} ${token('space.075', '6px')};
  }

  && input {
    padding: ${token('space.100', '8px')} ${token('space.075', '6px')}
      ${token('space.100', '8px')} 10px;
  }
`;

export interface DatabaseValueInlineEditStatelessProps {
  contentId: string;
  fieldId: string;
  entryId: string;
  isEditing: boolean;
  onIsEditingChange: (isEditing: boolean) => void;
  isReadonly?: boolean;
  isSelected?: boolean;
  markAsDeleted?: boolean;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  isFirstInRow?: boolean;
  matchHeaderPadding?: boolean;
  selectedAdjacentCells?: Adjacents;
  isLastInColumn?: boolean;
  enableSelection?: boolean;
  panel?: boolean;
  isHoverCell?: boolean;
  setIsHoverCell?: (isHover: boolean) => void;
  currentSortFieldId?: string;
  isRenderedInSidePanel?: boolean;
  hideAiSummaryCellAction?: boolean;
  showDisabledValue?: boolean;
  emptyValueLabel?: string;
  initialKey?: string;
}

/**
 * Renders an inline edit field to edit a database value. Automatically picks the right view/edit components based
 * on the field type of the value. When saving the changes, a database update is automatically drafted.
 */
const DatabaseValueInlineEditStateless: FunctionComponent<
  DatabaseValueInlineEditStatelessProps
> = ({
  contentId,
  fieldId,
  entryId,
  isEditing,
  onIsEditingChange,
  isReadonly,
  isSelected,
  markAsDeleted,
  onIsSelectedChange,
  isFirstInRow,
  matchHeaderPadding = false,
  selectedAdjacentCells,
  isLastInColumn,
  enableSelection,
  panel = false,
  isHoverCell = false,
  setIsHoverCell,
  currentSortFieldId,
  isRenderedInSidePanel,
  hideAiSummaryCellAction = false,
  showDisabledValue = false,
  emptyValueLabel,
  initialKey
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const versionId = getVersionId();
  const [isThreadPopupHovered, setIsThreadPopupHovered] = useState(false);
  const { setOpenThread, setFocusedCommentThreadId } = useCommentActionsContext();
  const { openThread } = useCommentStateContext();
  const { formatMessage } = useIntl();
  const [value, editMode, field, threads, totalCommentCount] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: true
      });
      const entry = getDatabaseEntryRequired(databaseDraft, entryId);
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);
      const value = getDatabaseValue(databaseDraft, field, entry);
      const editMode = getFieldType(field.type).editMode(
        { database: databaseDraft, field, entry, value },
        state
      );

      const threads = selectUnresolvedThreadsByValueIdOptional(state, {
        contentId,
        fieldId,
        entryId
      });
      const totalCommentCount = selectUnresolvedThreadsCommentCount(threads);

      return [value, editMode, field, threads, totalCommentCount] as const;
    });

  const handleReset = useCallback(
    (value: DatabaseValue) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId,
          entryId,
          value,
          change: null // ignore reset in collab mode, since we don't know what delta to pass?
        })
      );
    },
    [contentId, dispatch, entryId, fieldId]
  );
  const handleAiSummaryProcessingStart = useCallback(() => {
    onIsEditingChange(false);
  }, [onIsEditingChange]);

  const fieldSettingsRequired =
    (field.type === DatabaseFieldType.JIRA_ISSUE &&
      !field.settings.jiraServerId &&
      !field.settings.cloudId) ||
    (field.type === DatabaseFieldType.ENTRY_LINK &&
      !field.settings.databaseId &&
      !field.settings.contentId);

  const canEdit = !isReadonly && !fieldSettingsRequired;

  const isSkeletonEnabled = useSkeletonEnabled();
  const enableInlineEditMode =
    !canEdit || editMode === EditMode.READ_ONLY || isSkeletonEnabled
      ? false
      : isEditing && editMode === EditMode.INLINE_EDIT;

  const valueId = useMemo(() => {
    return getValueId(entryId, fieldId);
  }, [entryId, fieldId]);

  const toggleComment = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenThread({
        valueId: openThread.valueId === valueId ? undefined : valueId,
        panel: panel
      });
      // If manually toggled, we should clear the focused thread since it's derived from URL
      setFocusedCommentThreadId(undefined);
    },
    [openThread, valueId, panel, setOpenThread, setFocusedCommentThreadId]
  );

  const hasThreads = useMemo(() => threads && threads.length > 0, [threads]);
  const shouldShowHoverButton = useMemo(
    () => !isEditing && !isReadonly && (isHoverCell || openThread.valueId === valueId),
    [isEditing, isReadonly, isHoverCell, openThread.valueId, valueId]
  );
  const { isAiSummaryFieldEnabled, cellAction: aiSummaryCellAction } =
    useAiSummaryCellAction({
      contentId,
      entryId,
      field,
      isVisible: shouldShowHoverButton && !hideAiSummaryCellAction,
      value
    });
  const shouldUseAiSummaryReadView =
    isAiSummaryFieldEnabled && field.type === DatabaseFieldType.AI_SUMMARY;
  const { shouldRenderCommentInReadView } = useCommentsVisibility({
    versionId
  });

  const shouldShowTelepointers = !isSelected && field?.id && entryId;

  const { participants } = usePresence({
    grouping: field?.id ? { fieldId: field.id, entryId } : undefined,
    componentId: 'DatabaseValueInlineEdit',
    isEnabled: Boolean(shouldShowTelepointers)
  });

  return (
    <StyledInlineEditWrapper
      markAsDeleted={markAsDeleted}
      hideBorderOnFocus
      isSelected={isSelected}
      isFirstInRow={isFirstInRow}
      matchHeaderPadding={matchHeaderPadding}
      selectedAdjacentCells={selectedAdjacentCells}
      isLastInColumn={isLastInColumn}
      hasParticipants={participants.length > 0}
      isReadonly={isReadonly}
    >
      <ValidationStatusContainer>
        <AiSummaryProcessingBoundary
          contentId={contentId}
          entryId={entryId}
          fieldId={fieldId}
          isEnabled={shouldUseAiSummaryReadView}
          isTargetValueEmpty={
            value.type === DatabaseFieldType.AI_SUMMARY &&
            value.value.plain.trim().length === 0
          }
          onProcessingStart={handleAiSummaryProcessingStart}
        >
          {(loadingState) => {
            const isAiSummaryCellProcessing = loadingState !== null;

            return (
              <InlineEditStateless<DatabaseValue>
                isSelected={isSelected}
                isReadOnly={!canEdit || isAiSummaryCellProcessing}
                readView={() => (
                  <>
                    {shouldUseAiSummaryReadView ? (
                      <AiSummaryFieldReadView
                        contentId={contentId}
                        entryId={entryId}
                        fieldId={fieldId}
                        loadingState={loadingState}
                        // Prevent focussing anything inside the value (such as a link) by using Tab, as we want the Tab key
                        // to navigate between the inline-edit fields.
                        noFocus
                        isReadonly={!canEdit || isAiSummaryCellProcessing}
                        showDisabledValue={showDisabledValue}
                        emptyValueLabel={emptyValueLabel}
                      />
                    ) : (
                      <DatabaseValueView
                        contentId={contentId}
                        entryId={entryId}
                        fieldId={fieldId}
                        // Prevent focussing anything inside the value (such as a link) by using Tab, as we want the Tab key
                        // to navigate between the inline-edit fields.
                        noFocus
                        isReadonly={!canEdit}
                        showDisabledValue={showDisabledValue}
                        emptyValueLabel={emptyValueLabel}
                      />
                    )}
                    {shouldRenderCommentInReadView && (
                      <ThreadPopup
                        canEdit={canEdit}
                        contentId={contentId}
                        entryId={entryId}
                        fieldId={fieldId}
                        hasThreads={!!threads && threads.length > 0}
                        openThread={openThread}
                        panel={panel}
                        placement={panel ? 'left-start' : 'right-start'}
                        cellAction={
                          isAiSummaryCellProcessing ? undefined : aiSummaryCellAction
                        }
                        setIsHoverCell={setIsHoverCell}
                        setIsThreadPopupHovered={setIsThreadPopupHovered}
                        shouldShowCommentButton={!isAiSummaryCellProcessing}
                        shouldShowHoverButton={shouldShowHoverButton}
                        threads={threads}
                        toggleComment={toggleComment}
                        totalCommentCount={totalCommentCount}
                        valueId={valueId}
                      />
                    )}
                  </>
                )}
                editView={() => (
                  <DatabaseValueEdit
                    contentId={contentId}
                    entryId={entryId}
                    fieldId={fieldId}
                    noValidation={editMode === EditMode.INLINE_EDIT}
                    isSeamless={editMode === EditMode.EDIT}
                    autoFocus={isEditing}
                    currentSortFieldId={currentSortFieldId}
                    isRenderedInSidePanel={isRenderedInSidePanel}
                    initialKey={initialKey}
                  />
                )}
                isEditing={enableInlineEditMode && !isAiSummaryCellProcessing}
                onIsEditingChange={(nextIsEditing) => {
                  if (!nextIsEditing || !isAiSummaryCellProcessing) {
                    onIsEditingChange(nextIsEditing);
                  }
                }}
                value={value}
                onReset={handleReset}
                onIsSelectedChange={onIsSelectedChange}
                enableSelection={isThreadPopupHovered ? false : enableSelection}
                field={field}
                entryId={entryId}
                tooltipContent={
                  fieldSettingsRequired
                    ? formatMessage(
                        messages['value-inline-edit.required-field-missing-tooltip']
                      )
                    : null
                }
                hasThreads={!isEditing && hasThreads}
                shouldShowHoverButton={shouldShowHoverButton}
              />
            );
          }}
        </AiSummaryProcessingBoundary>
      </ValidationStatusContainer>
    </StyledInlineEditWrapper>
  );
};

export interface DatabaseValueInlineEditProps extends Omit<
  DatabaseValueInlineEditStatelessProps,
  'isEditing' | 'onIsEditingChange' | 'initialKey'
> {
  /** If true, the field will be in edit mode the first time it is rendered. */
  initialIsEditing?: boolean;
  onEditStart?: () => void;
  setEditingIds?: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;
}

const DatabaseValueInlineEdit: FunctionComponent<DatabaseValueInlineEditProps> = ({
  initialIsEditing = false,
  onEditStart,
  setEditingIds,
  ...props
}) => {
  const { entryId, fieldId } = props;
  const { fire: fireCellInlineEditMetric } = useMetric(cellInlineEditMetric);
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );
  // In the performance cohort, editing state is owned entirely by the selections machine
  // via the ping registry — the cell should never self-initialize to editing.
  // In the control cohort, initialIsEditing drives the initial state via editingIds.
  const [isEditing, setIsEditing] = useState(
    isSelectionsPerformanceEnabled ? false : initialIsEditing
  );
  const isEditingRef = useRef(isEditing);
  const initialKeyRef = useRef<string | undefined>(undefined);
  // Guards against the spurious blur fired by the focus-span unmounting when
  // the subscriber programmatically opens the editor. Without this, the form's
  // handleBlur would call onIsEditingChange(false) before the editor has had
  // a chance to focus its own input, immediately closing the cell.
  const justOpenedViaSubscriberRef = useRef(false);

  isEditingRef.current = isEditing;

  useEffect(() => {
    const k = key('cell', { entryId, fieldId });
    const myContext = props.isRenderedInSidePanel ? 'panel' : 'table';
    const unsubscribe = subscribeTo(k, 'editing', ({ active, context, initialKey }) => {
      initialKeyRef.current = active ? initialKey : undefined;

      const willOpen = active && context === myContext;
      if (willOpen && !isEditingRef.current) {
        onEditStart?.();
        justOpenedViaSubscriberRef.current = true;
        // Two animation frames cover ProseMirror's deferred focus (which may
        // dispatch focus events asynchronously) and any focus thrash between
        // body / span / contenteditable that would otherwise bubble up to the
        // form's handleBlur and prematurely close the cell.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            justOpenedViaSubscriberRef.current = false;
          });
        });
      }

      setIsEditing(willOpen);
    });

    return unsubscribe;
  }, [
    entryId,
    fieldId,
    onEditStart,
    props.isRenderedInSidePanel,
    isSelectionsPerformanceEnabled
  ]);

  useEffect(() => {
    if (initialIsEditing && !isEditing) {
      // Since we use editingIds only to set the initialIsEditing prop, it is enough to set it once during the initial
      // render of the inline edit field. We don't want the field to steal the focus again when it is hidden and then
      // shown again because of a filter change. Because of this, we remove the entry from the list once it has been
      // focused for the first time.
      setEditingIds?.((prev: Set<{ entryId: string; fieldId: string | undefined }>) => {
        const newSet = new Set(prev);

        newSet.forEach((item) => {
          if (item.entryId === props.entryId) {
            newSet.delete(item);
          }
        });

        return newSet;
      });
    }
  }, [initialIsEditing, isEditing, props.entryId, setEditingIds]);

  const handleIsEditingChange = useCallback(
    (editing: boolean) => {
      if (!editing && justOpenedViaSubscriberRef.current) {
        justOpenedViaSubscriberRef.current = false;
        return;
      }

      if (editing && !isEditingRef.current) {
        onEditStart?.();
      }

      if (
        editing &&
        !isEditingRef.current &&
        !getExperimentValue('databasesSelectionsPerformance', 'isEnabled', {
          fireExperimentExposure: false
        })
      ) {
        const startTime = performance.now();
        const tableRoot = document.querySelector<HTMLElement>(
          '[data-testid="layout-table"]'
        );

        fireCellInlineEditMetric({
          startTime,
          probeData: {
            tableRoot,
            entryId,
            fieldId
          },
          metadata: {
            contentId: props.contentId
          },
          ...(!tableRoot && {
            failImmediately: { reason: 'cell-inline-edit-dom-sync-missing-table-root' }
          })
        });
      }

      if (editing) {
        isEditingRef.current = true;
      } else {
        isEditingRef.current = false;
      }

      setIsEditing(editing);

      if (!editing && isSelectionsPerformanceEnabled) {
        const selectionsApi = getService('SelectionsApi');

        requestAnimationFrame(() => {
          selectionsApi.stopEditing();
        });
      }
    },
    [
      isSelectionsPerformanceEnabled,
      fireCellInlineEditMetric,
      entryId,
      fieldId,
      onEditStart,
      props.contentId
    ]
  );

  return (
    <DatabaseValueInlineEditStateless
      {...props}
      isEditing={isEditing}
      onIsEditingChange={handleIsEditingChange}
      initialKey={initialKeyRef.current}
    />
  );
};

export default React.memo(DatabaseValueInlineEdit);
